import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarIcon } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { Control, useFormContext } from 'react-hook-form';
import {
  Button,
  Calendar,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from 'ui';

export const FormFieldCalendar = ({
  key,
  path,
  defaultValue,
  label,
  value,
  field,
  itemClassName,
  labelClassName,
  placeholder = 'Selecciona una fecha',
  renderMessage,
}: {
  key?: string;
  path: string;
  defaultValue: Date | null;
  label: string;
  value: any;
  field?: {
    fromDate?: Date;
    setter?: (value: Date) => void;
  };
  itemClassName?: string;
  labelClassName?: string;
  placeholder?: string;
  renderMessage?: () => ReactNode;
}) => {
  const form = useFormContext();

  const [isOpen, setIsOpen] = useState(false);

  return (
    <FormField
      key={key}
      name={path}
      control={form.control}
      defaultValue={defaultValue}
      render={() => {
        return (
          <FormItem className={cn(itemClassName)}>
            <FormLabel className={labelClassName}>{label}</FormLabel>
            <Popover open={isOpen} onOpenChange={setIsOpen}>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    data-test-id-label={`date-picker-${label}`}
                    data-test-id='date-picker'
                    variant={'outline'}
                    className={cn(
                      'w-full pl-3 text-left font-normal border-primary placeholder:text-[.875rem] placeholder:text-[#B7B7C0] placeholder:font-normal',
                      !value && 'text-muted-foreground',
                    )}
                  >
                    {value ? (
                      format(new Date(value), 'PPP', {
                        locale: es,
                      })
                    ) : (
                      <span>{placeholder}</span>
                    )}
                    <CalendarIcon className='ml-auto h-4 w-4 opacity-50' />
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent
                className='w-auto p-0'
                align='start'
                onFocusOutside={(event) => event.preventDefault()}
              >
                <Calendar
                  fromDate={field?.fromDate}
                  toDate={new Date()}
                  mode='single'
                  selected={value}
                  onSelect={(day) => {
                    if (!day) {
                      setIsOpen(false);
                      return;
                    }

                    if (field?.setter) {
                      field.setter(day);
                    } else if (path) {
                      form.setValue(path, day);
                    }
                    setIsOpen(false);
                  }}
                  disabled={(date) =>
                    date > new Date() || date < new Date('1900-01-01')
                  }
                  initialFocus
                  className='placeholder:text-[.875rem] placeholder:text-[#B7B7C0] placeholder:font-normal'
                />
              </PopoverContent>
            </Popover>
            {renderMessage ? renderMessage() : <FormMessage />}
          </FormItem>
        );
      }}
    />
  );
};
